import clsx from 'clsx';
import HubertLabel from 'components/HubertUI/HubertLabel';
import MutedText from 'components/common/MutedText';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  selectQuestionIsReadonly,
  selectQuestionMinRequired,
  selectQuestionMode,
  selectTemplateMinRequired,
} from 'rtk/questionEditorSelector';
import { ECreateQuestionMode, useQuestionEditorContext } from '../context/create-question.context';
import { updateMinRequired } from 'rtk/Question.Editor.Slice';
import { useTemplateId } from 'hooks/useTemplate';
import { updateCustomParametersInTemplate } from 'rtk/questionEditorThunks';
import { useRef, useEffect } from 'react';
import DebouncedInput from 'components/common/DebouncedInput';
import { useTranslation } from 'react-i18next';

const NumberSettingInput = () => {
  const { t } = useTranslation('Questionsv2');
  const { questionId } = useQuestionEditorContext();
  const templateId = useTemplateId();
  const dispatch = useAppDispatch();
  const minRequired = useAppSelector((state) => selectQuestionMinRequired(state, questionId));
  const templateMinRequired = useAppSelector((state) => selectTemplateMinRequired(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const disabled = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const updatedQuestion = useAppSelector((state) => state.Question.updateQuestion);

  const isTemplateEdit = mode === ECreateQuestionMode.InTemplateEdit;
  const originalQuestionMinRequired = useRef<number | undefined>(minRequired);

  // Sync question state with template value on mount in template edit mode
  useEffect(() => {
    if (isTemplateEdit && templateMinRequired !== undefined && templateMinRequired !== minRequired) {
      dispatch(updateMinRequired({ questionId, value: templateMinRequired }));
    }
  }, [isTemplateEdit, templateMinRequired, minRequired, questionId, dispatch]);

  // Update placeholder baseline only when the question was explicitly saved
  useEffect(() => {
    if (!updatedQuestion) return;
    if (updatedQuestion.id !== questionId) return;
    originalQuestionMinRequired.current = (updatedQuestion as { min_required?: number }).min_required;
  }, [updatedQuestion, questionId]);

  const inputValue = isTemplateEdit
    ? templateMinRequired === undefined
      ? ''
      : templateMinRequired
    : (minRequired ?? '');
  const placeholder =
    isTemplateEdit && originalQuestionMinRequired.current !== undefined
      ? t('numberSetting.placeholderWithDefault', { value: originalQuestionMinRequired.current })
      : t('numberSetting.placeholderNoMin');

  const parseNumberOrUndefined = (val: string | number | null | undefined): number | undefined => {
    if (val === '' || val === undefined || val === null) return undefined;
    const num = typeof val === 'number' ? val : parseInt(val, 10);
    return isNaN(num) ? undefined : num;
  };

  const handleNumberChange = (val: string | number) => {
    const value = parseNumberOrUndefined(val);
    const currentValue = isTemplateEdit ? templateMinRequired : minRequired;
    if (value === currentValue) return;
    dispatch(updateMinRequired({ questionId, value }));
    updateCustomParametersInTemplate(
      templateId,
      questionId,
      dispatch,
      mode,
      value,
      'min_required',
      value === undefined
        ? `User cleared min_required of ${questionId}.`
        : `User updated min_required of ${questionId} to ${value}.`,
    );
  };

  return (
    <div className={clsx('flex flex-col justify-start gap-4', 'p-1')}>
      <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
        <HubertLabel
          htmlFor="create-question-min-required"
          className={clsx('whitespace-nowrap', disabled && 'opacity-50')}
        >
          {t('numberSetting.label')}
        </HubertLabel>
        <DebouncedInput
          id="create-question-min-required"
          type="number"
          disabled={disabled}
          value={inputValue}
          min={0}
          placeholder={placeholder}
          onChange={handleNumberChange}
        />
      </div>
      <div>
        <MutedText text={t('numberSetting.hint')} />
      </div>
    </div>
  );
};

export default NumberSettingInput;
